import React, { useEffect, useRef, useState } from 'react';
import Message from "../../components/Message";
import ResizableTextarea from "../../components/ResizableTextarea";

import "./chatcontainer.css"
import instance from "../../api.config";
import { socket } from "../../socket";
import useChatMessages from "./useChatMessages";

function ChatContainer({ currentChat, currentUser, onMessagesRead }) {
    const { messages, newMsgCount, acceptSavedMessage, isCurrentChat } = useChatMessages(currentChat?.id, currentUser.id);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newMessage, setNewMessage] = useState("")
    const [image] = useState("")
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedImageBase64, setSelectedImageBase64] = useState(null);
    const scrollRef = useRef();
    const scrollContainerRef = useRef();
    const fileInputRef = useRef(null);
    const [isTyping, setIsTyping] = useState(false);
    const [typingUsers, setTypingUsers] = useState([]);
    const typingTimeoutRef = useRef(null);

    useEffect(() => {
        // Listen for typing indicators
        const handleUserTypingInChat = ({chatId, userId, nickname, isTyping: typing}) => {
            if (Number(chatId) === Number(currentChat?.id) && Number(userId) !== Number(currentUser.id)) {
                setTypingUsers(prev => {
                    if (typing) {
                        // Add user to typing list if not already there
                        const existingUser = prev.find(user => user.userId === userId);
                        if (!existingUser) {
                            return [...prev, { userId, nickname }];
                        }
                        return prev;
                    } else {
                        // Remove user from typing list
                        return prev.filter(user => user.userId !== userId);
                    }
                });
            }
        };

        socket.on("userTyping", handleUserTypingInChat);

        return () => {
            setTypingUsers([]);
            socket.off("userTyping", handleUserTypingInChat);
        }

    }, [currentChat?.id, currentUser.id])


    // Автоматична прокрутка при завантаженні повідомлень
    useEffect(() => {
        if (messages.length > 0 && scrollContainerRef.current) {
            // Чекаємо поки DOM оновиться
            const timeout = setTimeout(() => {
                const scrollContainer = scrollContainerRef.current;
                if (!scrollContainer) return;

                if (newMsgCount > 0) {
                    // Якщо є нові повідомлення, прокручуємо до них
                    const children = Array.from(scrollContainer.children[0].children);
                    const firstNewMessageIndex = children.length - newMsgCount;
                    
                    if (firstNewMessageIndex >= 0 && children[firstNewMessageIndex]) {
                        // Прокручуємо до першого нового повідомлення
                        const firstNewMessage = children[firstNewMessageIndex];
                        firstNewMessage.scrollIntoView({ behavior: 'auto', block: 'start' });
                    } else {
                        // Якщо не можемо знайти перше нове повідомлення, прокручуємо в кінець
                        scrollContainer.scrollTop = scrollContainer.scrollHeight;
                    }
                } else {
                    // Якщо нових повідомлень немає, прокручуємо в самий кінець
                    scrollContainer.scrollTop = scrollContainer.scrollHeight;
                }
            }, 100);
            return () => clearTimeout(timeout);
        }
    }, [messages.length, newMsgCount])

    useEffect(() => {
        const fetch = async () => {

            if (!currentChat?.id) return;

            try {
                await instance.post('/supportReadMessages/', { chatId: currentChat.id });
                onMessagesRead?.(currentChat.id);
            } catch (error) {
                console.error(error);
            }
        }

        if (currentChat?.id) {
            fetch();
        }

    }, [messages, currentChat?.id, onMessagesRead])

    // Cleanup typing indicator when component unmounts or chat changes
    useEffect(() => {
        return () => {
            if (isTyping && currentChat?.id) {
                socket.emit("typing", {
                    chatId: currentChat.id,
                    userId: currentUser.id,
                    isTyping: false
                });
                if (typingTimeoutRef.current) {
                    clearTimeout(typingTimeoutRef.current);
                }
            }
        };
    }, [currentChat?.id, isTyping, currentUser.id])

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;


        if (newMessage.length > 1024) {
            alert('Розмір поля вводу перевищує максимально допустимий розмір в 1000 символів.')
            return;
        }

        if (newMessage.trim() !== "" || selectedImage) {

            setIsSubmitting(true);

            const submittedChatId = currentChat.id;
            const formData = new FormData();
            formData.append('chatId', submittedChatId);
            formData.append('senderId', currentUser.id);
            formData.append('text', newMessage);
            formData.append('image', selectedImage);

            try {
                const response = await instance.post('/createMessage', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                // A socket echo can arrive before the HTTP response, including replies
                // sent through Telegram by this same support account.
                if (response.data?.id == null || !response.data.createdAt) {
                    throw new Error('Сервер не підтвердив збереження повідомлення');
                }
                acceptSavedMessage({
                    senderId: currentUser.id,
                    text: newMessage,
                    chatId: submittedChatId,
                    nickname: currentUser.nickname,
                    main_url: currentUser.main_url,
                    is_manager: currentUser.is_manager,
                    ...response.data
                });
                // A response for a previously open chat must not alter the new chat's draft.
                if (!isCurrentChat(submittedChatId)) return;

                // Очищаємо форму тільки після успішної відправки
                setNewMessage('');
                setSelectedImage(null);
                setSelectedImageBase64(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }

            } catch (e) {
                console.error(e);
                alert(`Send message error! ${e.message}`)
            } finally {
                setIsSubmitting(false);
            }
        } else {
            // Якщо поле порожнє, просто знімаємо блокування
            setIsSubmitting(false);
        }
    };

    const convertImageToBase64 = (image) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(image);
        });
    };

    // Function to emit typing status
    const emitTyping = (typing) => {
        if (currentChat?.id) {
            socket.emit("typing", {
                chatId: currentChat.id,
                userId: currentUser.id,
                nickname: currentUser.nickname,
                isTyping: typing
            });
        }
    };

    // Handle message input change with typing indicator
    const handleMessageChange = (e) => {
        setNewMessage(e.target.value);
        
        // Start typing
        if (!isTyping) {
            setIsTyping(true);
            emitTyping(true);
        }

        // Clear previous timeout
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
        }

        // Stop typing after 2 seconds of inactivity
        typingTimeoutRef.current = setTimeout(() => {
            setIsTyping(false);
            emitTyping(false);
        }, 2000);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            
            // Stop typing when sending message
            if (isTyping) {
                setIsTyping(false);
                emitTyping(false);
                if (typingTimeoutRef.current) {
                    clearTimeout(typingTimeoutRef.current);
                }
            }
            
            handleSubmit(e);
        }
    };


    return (
        <div className='d-flex flex-grow-1' style={{maxHeight: "100vh"}}>
            <div className="message-container ">
                <div className="container-fluid d-flex flex-column">
                    {
                        currentChat ? <>
                            <div ref={scrollContainerRef} className="row d-flex justify-content-center flex-grow-1 overflow-auto">
                                <fieldset className="chat-box">
                                    {messages.map((m => (
                                        <div ref={scrollRef} key={m.id}>
                                            <Message message={m} own={Number(m.senderId) === Number(currentUser.id)}
                                                ownImage={currentUser.image} image={image}
                                                currentUserData={currentUser}/>
                                        </div>
                                    )))
                                    }
                                </fieldset>
                                {typingUsers.length > 0 && (
                                        <div className="typing-indicator">
                                            {typingUsers.length === 1 
                                                ? `${typingUsers[0].nickname} набирає повідомлення...`
                                                : typingUsers.length === 2 
                                                    ? `${typingUsers[0].nickname} і ${typingUsers[1].nickname} набирають повідомлення...`
                                                    : `${typingUsers[0].nickname} і ${typingUsers[1].nickname} та ще ${typingUsers.length - 2} набирають повідомлення...`
                                            }
                                        </div>
                                    )}
                            </div>
                            <div className="row d-flex justify-content-center align-items-center p-1">
                                <div className="col-6 text-end">
                                    <ResizableTextarea
                                        placeholder="Send a message"
                                        name="newMessage"
                                        onChange={handleMessageChange}
                                        value={newMessage}
                                        onKeyPress={handleKeyPress}
                                        minWidth={400}
                                        minHeight={40}
                                        maxHeight={200}
                                        resizeMode="vertical"
                                    />
                                </div>
                                <div className="col-3 d-flex justify-content-start align-items-center">

                                    <label className='image-label transition' htmlFor="fileInput">
                                        {selectedImage ? (
                                            <img src={selectedImageBase64} alt="" style={{ width: 60 }} />
                                        ) : (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                height="30px"
                                                viewBox="0 0 24 24"
                                                width="30px"
                                                fill="#000000"
                                            >
                                                <path d="M0 0h24v24H0V0z" fill="none" />
                                                <path d="M18 20H4V6h9V4H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-9h-2v9zm-7.79-3.17l-1.96-2.36L5.5 18h11l-3.54-4.71zM20 4V1h-2v3h-3c.01.01 0 2 0 2h3v2.99c.01.01 2 0 2 0V6h3V4h-3z" />
                                            </svg>
                                        )}
                                    </label>
                                    <input
                                        id="fileInput"
                                        type="file"
                                        ref={fileInputRef}
                                        style={{ display: 'none' }}
                                        accept="image/*"
                                        onChange={async (e) => {
                                            setSelectedImage(e.target.files[0]);
                                            setSelectedImageBase64(e.target.files[0] ? await convertImageToBase64(e.target.files[0]) : null);
                                        }}
                                    />
                                    <button className="send-icon mx-2 transition" onClick={handleSubmit}>
                                        <i className={`fa ${isSubmitting ? 'fa-spinner rotating-container' : 'fa-paper-plane'}`} />
                                    </button>
                                </div>
                            </div>
                        </> : <span>Open a conversation to start a chat</span>}
                </div>
            </div>
        </div>
    );
}

export default ChatContainer;
